import { ROUTES } from '../constants/routes';
import {
  canAccessOrgMembersMenu,
  canAccessOrgRolesMenu,
  canAccessBilling,
  canAccessPlatformOrganizations,
  canAccessPlatformUsersMenu,
  canAccessTenantMoreMenu,
  canAccessWorkspace,
  userHasAnyOrgPermission,
  userHasAnyPlatformPermission,
} from '../utils/orgPermissionAccess';
import { isOrgOwner } from '../utils/orgAccess';
import { isPlatformSuperAdmin } from '../utils/authAccess';

/** @typedef {{ user: object|null, activeOrganization?: object|null, organizations?: object[] }} PanelAccessContext */

const TENANT_MORE_ROUTES = new Set([
  ROUTES.AGENDA_TEMPLATES,
  ROUTES.FILES,
  ROUTES.MINUTES,
  ROUTES.TRANSCRIPTS,
  ROUTES.STT_JOBS,
]);

/** @type {Record<string, string[]>} */
export const MENU_ORG_PERMISSIONS = {
  [ROUTES.DASHBOARD]: [],
  [ROUTES.MEETINGS]: ['meetings.view'],
  [ROUTES.MEETING_GROUPS]: ['meeting_groups.view', 'meetings.view'],
  [ROUTES.ROOMS]: ['rooms.view'],
  [ROUTES.AGENDA_TEMPLATES]: ['agenda.view', 'agenda.templates'],
  [ROUTES.FILES]: ['files.view'],
  [ROUTES.ACTION_ITEMS]: ['action_items.view'],
  [ROUTES.MINUTES]: ['minutes.view'],
  [ROUTES.TRANSCRIPTS]: ['transcripts.view', 'transcripts.view-any'],
  [ROUTES.STT_JOBS]: ['stt.view', 'stt.view-any'],
  [ROUTES.CALENDAR]: ['meetings.view', 'rooms.view'],
  [ROUTES.REPORTS]: ['reports.view'],
  [ROUTES.BILLING]: ['payments.view', 'subscriptions.view', 'wallet.view'],
  [ROUTES.SEARCH]: [
    'meetings.view',
    'rooms.view',
    'files.view',
    'action_items.view',
    'minutes.view',
    'agenda.view',
  ],
  [ROUTES.WORKSPACE]: ['organizations.view', 'organizations.update', 'users.view', 'departments.view'],
  [ROUTES.ORG_MEMBERS]: ['users.view', 'users.manage'],
  [ROUTES.ORG_ROLES]: ['roles.view', 'roles.manage'],
};

export function canViewAuditEvents(user, organizations = null, activeOrganization = null) {
  if (!user) return false;
  if (isPlatformSuperAdmin(user)) return true;
  if (isOrgOwner(organizations, activeOrganization?.id)) return true;
  return userHasAnyOrgPermission(user, ['audit.view']);
}

/** @type {Record<string, string[]>} */
export const MENU_PLATFORM_PERMISSIONS = {
  [ROUTES.ORGANIZATIONS]: [
    'organizations.view',
    'organizations.manage',
    'organizations.create',
    'organizations.update',
  ],
  [ROUTES.USERS]: ['users.manage'],
  [ROUTES.SETTINGS]: [],
  [ROUTES.ROLES_AND_PERMISSIONS]: [],
};

/**
 * @param {string} routeKey
 * @param {PanelAccessContext} ctx
 */
export function isMenuItemVisible(routeKey, ctx) {
  const { user, organizations, activeOrganization } = ctx;
  if (!user) return false;
  // اطلاعیه‌ها موقتاً از منو پنهان است (مسیر /announcements و API فعال می‌مانند)
  if (routeKey === ROUTES.ANNOUNCEMENTS) return false;

  if (routeKey === ROUTES.PROFILE || routeKey === ROUTES.SECURITY || routeKey === ROUTES.DASHBOARD) return true;

  // رونوشت‌ها: فقط سوپرادمین (سازمان/کاربر عادی UI مشاهده ندارند)
  if (routeKey === ROUTES.TRANSCRIPTS) {
    return isPlatformSuperAdmin(user);
  }

  if (routeKey === ROUTES.SETTINGS || routeKey === ROUTES.ROLES_AND_PERMISSIONS) {
    return isPlatformSuperAdmin(user);
  }

  if (routeKey === ROUTES.ORGANIZATIONS) {
    return canAccessPlatformOrganizations(user);
  }

  if (routeKey === ROUTES.WORKSPACE) {
    return canAccessWorkspace(user, organizations, activeOrganization);
  }

  if (routeKey === ROUTES.BILLING) {
    return canAccessBilling(user, organizations, activeOrganization);
  }

  if (TENANT_MORE_ROUTES.has(routeKey)) {
    if (!canAccessTenantMoreMenu(user, organizations, activeOrganization)) {
      return false;
    }
  }

  if (routeKey === ROUTES.ORG_MEMBERS) {
    return canAccessOrgMembersMenu(user, organizations, activeOrganization);
  }

  if (routeKey === ROUTES.ORG_ROLES) {
    return canAccessOrgRolesMenu(user, organizations, activeOrganization);
  }

  if (routeKey === ROUTES.USERS) {
    return canAccessPlatformUsersMenu(user);
  }

  if (routeKey === ROUTES.WEBHOOK_DEAD_LETTERS) {
    return isPlatformSuperAdmin(user);
  }

  if (routeKey === ROUTES.AUDIT_EVENTS) {
    return canViewAuditEvents(user, organizations, activeOrganization);
  }

  if (isPlatformSuperAdmin(user)) {
    return true;
  }

  const orgPerms = MENU_ORG_PERMISSIONS[routeKey];
  if (orgPerms?.length) {
    return userHasAnyOrgPermission(user, orgPerms);
  }

  const platformPerms = MENU_PLATFORM_PERMISSIONS[routeKey];
  if (platformPerms?.length) {
    return userHasAnyPlatformPermission(user, platformPerms);
  }

  return true;
}

/**
 * @param {PanelAccessContext} ctx
 * @returns {string}
 */
export function getDefaultPostLoginRoute(ctx) {
  const { user } = ctx;
  if (!user) return ROUTES.LOGIN;
  if (isMenuItemVisible(ROUTES.DASHBOARD, ctx)) return ROUTES.DASHBOARD;
  if (canAccessPlatformOrganizations(user)) return ROUTES.ORGANIZATIONS;
  if (isMenuItemVisible(ROUTES.MEETINGS, ctx)) return ROUTES.MEETINGS;
  const candidates = [
    ROUTES.MEETINGS,
    ROUTES.BILLING,
    ROUTES.ROOMS,
    ROUTES.FILES,
    ROUTES.PROFILE,
    ROUTES.WORKSPACE,
  ];
  const first = candidates.find((r) => isMenuItemVisible(r, ctx));
  return first || ROUTES.PROFILE;
}

/**
 * @param {PanelAccessContext} ctx
 * @param {string} pathname
 */
export function isRouteAccessible(pathname, ctx) {
  const { user } = ctx;
  if (!user) return false;
  if (pathname === ROUTES.DASHBOARD) return true;
  if (pathname === ROUTES.TRANSCRIPTS || pathname.startsWith(`${ROUTES.TRANSCRIPTS}/`)) {
    return isPlatformSuperAdmin(user);
  }
  if (pathname.startsWith('/settings')) return isPlatformSuperAdmin(user);
  if (pathname.startsWith('/organizations')) return canAccessPlatformOrganizations(user);
  if (pathname === ROUTES.WORKSPACE || pathname.startsWith('/workspace/')) {
    return canAccessWorkspace(user, ctx.organizations, ctx.activeOrganization);
  }
  if (pathname.startsWith('/users')) {
    return canAccessPlatformUsersMenu(user);
  }
  if (pathname === ROUTES.AUDIT_EVENTS || pathname.startsWith(`${ROUTES.AUDIT_EVENTS}/`)) {
    return canViewAuditEvents(user, ctx.organizations, ctx.activeOrganization);
  }
  if (pathname === ROUTES.ORG_MEMBERS || pathname.startsWith(`${ROUTES.ORG_MEMBERS}/`)) {
    return canAccessOrgMembersMenu(user, ctx.organizations, ctx.activeOrganization);
  }
  if (pathname === ROUTES.ORG_ROLES || pathname.startsWith(`${ROUTES.ORG_ROLES}/`)) {
    return canAccessOrgRolesMenu(user, ctx.organizations, ctx.activeOrganization);
  }

  if (pathname === ROUTES.BILLING || pathname.startsWith(`${ROUTES.BILLING}/`)) {
    return canAccessBilling(user, ctx.organizations, ctx.activeOrganization);
  }

  const routeEntries = Object.entries(MENU_ORG_PERMISSIONS);
  for (const [route, perms] of routeEntries) {
    if (pathname === route || pathname.startsWith(`${route}/`)) {
      if (isPlatformSuperAdmin(user)) return true;
      return userHasAnyOrgPermission(user, perms);
    }
  }

  if (pathname === ROUTES.PROFILE || pathname.startsWith('/profile')) return true;
  if (pathname === ROUTES.SECURITY || pathname.startsWith(`${ROUTES.SECURITY}/`)) return true;

  const tenantMoreBlocked = !canAccessTenantMoreMenu(user, ctx.organizations, ctx.activeOrganization);
  if (tenantMoreBlocked) {
    for (const route of TENANT_MORE_ROUTES) {
      if (pathname === route || pathname.startsWith(`${route}/`)) {
        return false;
      }
    }
  }

  return true;
}
